import { Component, Input } from '@angular/core';
import { ButtonComponent } from "@shared/controls/button/button.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { EmailPreviewComponent } from "@shared/components/email-preview/email-preview.component";
import {
  MultipleInputEmailComponent
} from "@shared/controls/multiple-input-email/multiple-input-email.component";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { FileUploadComponent } from "@shared/controls/file-upload/file-upload.component";
import { NotificationComponent } from "@shared/components/commons/notification/notification.component";
import { catchError, EMPTY, filter, Observable, switchMap, tap } from "rxjs";
import { LodgeDocumentsComponent } from "../lodge-documents/lodge-documents.component";
import { DatePipe, formatDate } from "@angular/common";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SignDocumentsService } from './sign-documents.service';
import { Document } from "@models/documents/document";
import { SendOption, SigningStatus } from '@models/annual-statements/annual-statement-enums';
import { FileLabelComponent } from "@shared/components/commons/file-label/file-label.component";
import { SendByRadioComponent } from "@shared/components/documents-related/send-by-radio/send-by-radio.component";
import { IntegrationConnectionsEnum } from "@models/integrations/integration-connections.enum";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { ReminderSendingType } from '@models/reminders/reminder.enum';
import { AsicStatus } from '@models/documents/asic-status.enum';
import { DocumentSigning } from '@models/documents/document-signing';
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";

@Component({
  selector: 'app-sign-documents',
  standalone: true,
  imports: [
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    EmailPreviewComponent,
    MultipleInputEmailComponent,
    DatePickerComponent,
    FileUploadComponent,
    NotificationComponent,
    LodgeDocumentsComponent,
    DatePipe,
    FileLabelComponent,
    SendByRadioComponent,
    CheckboxComponent,
    DisclaimerComponent,
    ListGridClientSideComponent
  ],
  templateUrl: 'sign-documents.component.html',
  styleUrl: 'sign-documents.component.scss',
  providers: [EmailsApiService],
})
export class SignDocumentsComponent extends SignDocumentsService {
  @Input() documentStatus: DocumentStatusEnum | undefined;
  @Input() isProviderConnectionStatusLoading = false;
  @Input() asicStatus!: AsicStatus;
  @Input() override selectedESignConnection: IntegrationConnectionsEnum | null = null;

  protected readonly DocumentStatusEnum = DocumentStatusEnum;
  readonly AsicStatus = AsicStatus;

  signingMinDate: NgbDateStruct = DatepickerHelper.getTodayStruct();
  signingMaxDate: NgbDateStruct = DatepickerHelper.getTodayStruct();

  override ngOnInit(): void {
    this.loadAsicFormSigningSettingReminders();
    super.ngOnInit();

    this.signingMinDate = this.documentBase.createdOn ? DatepickerHelper.getStructFromDate(this.documentBase.createdOn) : DatepickerHelper.getTodayStruct();
  }

  override disableHeaderBtn(value: boolean): void {
    this.documentsService.disabledHeaderBtn.set(value);
  }

  override showVoidEnvelope(value: boolean): void {
    this.documentsService.showVoidEnvelope.set(value);
  }

  override showSendEmailBtn(value: boolean): void {
    this.documentsService.showSendEmailBtn.set(value);
  }

  override showSendPaperBtn(value: boolean): void {
    this.documentsService.showSendPaperBtn.set(value);
  }

  override sendSignLoading(value: boolean): void {
    this.documentsService.sendSignLoading.set(value);
  }

  override sendToESign(): Observable<DocumentSigning> {
    return super.sendToESign().pipe(
      tap(() => {
          const document = this.documentBase as Document;
          if(document?.lodgementDeadline && document.lodgementDeadline < new Date()){
            this.toastr.warning('No reminder was created because the payment deadline has passed. To set a reminder, click the \'Add Reminder\' button on the left side.');
        }
      }));
  }

  override setSendByOption(): void {
    super.setSendByOption();
    if (this.form.controls.sendBy.value === SendOption.Sign && this.asicStatus !== AsicStatus.NotLodged) {
      this.documentsService.showNextBtn.set(true);
      this.sendByOptions.filter(o => o.value === SendOption.Paper)[0].disabled = true;
    }
  }

  override listenSendByButton(): void {
    this.form.controls.sendBy.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sendBy: SendOption | null) => {
         this.documentsService.showNextBtn.set(false);
        if (sendBy === SendOption.Sign) {
          this.setSignatories();
          if(this.asicStatus !== AsicStatus.NotLodged) {
            this.disableHeaderBtn(false);
            this.documentsService.showNextBtn.set(true);
          }
        } else if (sendBy === SendOption.Paper) {
          this.disableHeaderBtn(false);
        } else {
          const docuemnt = this.documentBase as Document;
          if (docuemnt && docuemnt.documentStatus && docuemnt.documentStatus > DocumentStatusEnum.SignaturePending) {
            this.disableHeaderBtn(this.documentSigning?.signingStatus !== SigningStatus.Signed);
          }
        }

        this.showSendEmailBtn(sendBy === SendOption.Email);
        this.showSendPaperBtn(sendBy === SendOption.Paper);
      });
  }

  override listenHeaderButton(): void {
    super.listenHeaderButton();

    this.confirmNotLodgedFormAction$
      ?.pipe(
        filter(confirm => confirm),
        switchMap(() => {
          this.sendSignLoading(true);
          return this.documentsApiService.confirmDocument(this.documentBase.documentId).pipe(
            catchError(err => {
              this.toastr.error('Change document status error', 'Error');
              console.error(err);
              this.sendSignLoading(false);
              return EMPTY;
            })
          ) as unknown as Observable<Document>;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.toastr.success('Document status changed successfully', 'Success');
          this.nextStep.emit(true);
          this.confirmNotLodgedFormAction$?.next(false);
          this.sendSignLoading(false);
        }
      });
  }

  override getSendEmailNotification(): string {
    if (!this.documentBase.notificationDetails?.sentToEmails || this.documentBase.notificationDetails.sentToEmails.length === 0) {
      return '';
    }

    const emailList = this.documentBase.notificationDetails.sentToEmails.join(', </b><b>');
    const currentDate = formatDate(this.documentBase.notificationDetails?.sentOn ?? new Date(), 'dd MMM yyyy', 'en-US');
    return `The documents have been emailed to <b>${ emailList }</b> on <b>${ currentDate }</b>`;
  }

  loadAsicFormSigningSettingReminders() {
    return this.settingsService.getReminderSettings()
      .pipe(
        tap(settingsData => {
          this.asicFormSigningSettingReminderSendingType = settingsData.asicFormSigningSetting.sendingType;
          this.isPhoneNumberRequired = [ReminderSendingType.SMS, ReminderSendingType.EmailAndSMS].includes(settingsData.asicFormSigningSetting.sendingType);

          if(this.isEnvelopeSent){
            this.setEnvelopeGridConfig();
          } else {
            this.setGridConfig();
          }
        }),
        catchError(() => {
          this.isPhoneNumberRequired = false;
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
